import { FC } from "react";
import { Button, Grid, Stack, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { useNavigate } from "react-router-dom";
import { NavigateIcon } from "../../assets/icons";
import { numericFormatter } from "../../common/utils";
import { useBreadcrumbs } from "../../context/BreadcrumbsProvider";
import DropdownSelect, { DropdownSelectObject } from "../common/DropdownSelect";
import { ChartProps, DataPoint } from "./types";

interface Props extends Omit<ChartProps<DataPoint[]>, "onLabelClicked" | "onWidthChanged" | "renderOptions"> {
	reportDownload?: Array<{ title: string; url: string }>;
	barGuideTransparency?: string;
	renderSelectDropdown?: React.ReactNode;
	chartMode: "tCO2e" | "% tCO2e";
	onChartModeChange: (v: "tCO2e" | "% tCO2e") => void;
}

const SimpleBarChart: FC<Props> = ({
	data,
	barGuideTransparency = "20",
	renderSelectDropdown,
	chartMode,
	onChartModeChange,
}) => {
	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();

	const menuSelect: DropdownSelectObject[] = [
		{ key: "tCO2e", label: "tCO2e" },
		{ key: "% tCO2e", label: "% tCO2e" },
	];

	const numeric = numericFormatter("decimal", 2, 2);

	return (
		<Grid container spacing={1}>
			<Grid size={12} justifyContent={"flex-end"} display={"flex"}>
				<Stack spacing={2} direction={"row"} alignItems={"center"}>
					<Typography variant="body2" color="text.secondary" component="div">
						{renderSelectDropdown && renderSelectDropdown}
					</Typography>
					<DropdownSelect
						value={chartMode}
						onChange={(v) => onChartModeChange(v as "tCO2e" | "% tCO2e")}
						dropdownItems={menuSelect}
						variant="outlined"
					/>
				</Stack>
			</Grid>

			{data.map((p, k) => {
				const handleNavigate = () => {
					if (!p.link) return;

					visitPage(p.link.pageType);
					navigate(p.link.route);
				};

				return (
					<Grid container key={k} size={12} spacing={1} minHeight={"38px"} alignItems="center">
						<Grid size={3} alignItems={"center"} display={"flex"}>
							{p.link ? (
								<Button
									fullWidth
									sx={{ marginLeft: -1, justifyContent: "space-between" }}
									onClick={handleNavigate}
									endIcon={<NavigateIcon />}
								>
									<Typography
										variant="caption"
										textAlign={"left"}
										fontWeight={"bold"}
										textTransform={"uppercase"}
									>
										{p.label}
									</Typography>
								</Button>
							) : (
								<Typography variant="caption" fontWeight={"bold"} textTransform={"uppercase"}>
									{p.label}
								</Typography>
							)}
						</Grid>
						<Grid size="grow">
							<LinearProgress
								variant="determinate"
								value={p.percentage ?? 0}
								sx={{
									backgroundColor: p.color ? `${p.color}${barGuideTransparency}` : undefined,
									"& .MuiLinearProgress-bar": { backgroundColor: p.color },
								}}
								className="chart-bar"
							/>
						</Grid>

						<Grid size={3} sx={{ textAlign: "end" }}>
							{chartMode === "tCO2e" && (
								<Typography variant="h5" color="primary">
									{numeric.format(p.value)}
								</Typography>
							)}
							{chartMode === "% tCO2e" && (
								<Typography variant="h5" color="primary">
									{p.percentage ? p.percentage.toFixed(2) : 0}
								</Typography>
							)}
						</Grid>
					</Grid>
				);
			})}
		</Grid>
	);
};

export default SimpleBarChart;
